import { CommonModule } from "@angular/common";
import { Component, ViewChild } from "@angular/core";
import { FormGroup, FormsModule } from "@angular/forms";
import { MatRadioModule } from "@angular/material/radio";
import {
    AddressDetailsAttributes,
    BaseAddress,
    NullableAddresses,
    NullableRegisteredUserDetails,
    UserDetailStatus,
} from "@models/user.model";
import { AddressComponent } from "../../user-details/address/address.component";
import { UserService } from "../../services/user/user.service";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { finalize } from "rxjs";
import { GenericDialogComponent } from "../../shared/generic-dialog/generic-dialog.component";
import { DialogInvokingComponents } from "@models/generic-dialog.model";
import { SnackBarConfig } from "../../constants/snackbar.constants";
import { DialogService } from "../../services/dialog/dialog.service";
import { Router } from "@angular/router";

@Component({
    selector: "app-address-details",
    standalone: false,
    templateUrl: "./address-details.component.html",
    styleUrl: "./address-details.component.scss",
})
export class AddressDetailsComponent {
    public userDetails!: NullableRegisteredUserDetails;
    public profileForm!: FormGroup;
    public isEmailValid = true;
    public editMode = false;
    public selectedAddress!: AddressDetailsAttributes;
    public addMode = false;
    public address!: AddressDetailsAttributes;
    public showSubmitButton = false;
    public showEditIcon = true;
    public showDiv = true;
    public showProfileImage!: string | undefined;
    public image!: string;
    public profileLabel = UserDetailStatus;
    public isLoading: boolean = true;
    public activeSection: string = "profile";
    public currentUserAddresses?: NullableAddresses;

    @ViewChild(AddressComponent) addressDetailRef: AddressComponent | undefined;

    constructor(
        private userService: UserService,
        private dialog: MatDialog,
        private _snackBar: MatSnackBar,
        private translate: TranslateService,
        private dialogService: DialogService,
        private router: Router,
    ) {}

    ngOnInit(): void {
        this.getUserDetails();
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    private getUserDetails(): void {
        this.isLoading = true;
        this.userService
            .getUserDetails()
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                }),
            )
            .subscribe((response) => {
                this.userDetails = response;
                this.currentUserAddresses = response?.addresses!;
                this.selectedAddress = response?.addresses?.find((one) => one.isPreferred)!;
                this.showProfileImage = response?.user.profileImage;
            });
    }

    public deleteAddress(address: AddressDetailsAttributes) {
        this.openDialog(address);
    }

    private openDialog(address: AddressDetailsAttributes): void {
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: {
                componentName: DialogInvokingComponents.DeleteAddress,
                title: this.getTranslation("genericDialogBox.deleteAddress.title"),
                content: this.getTranslation("genericDialogBox.deleteAddress.content"),
                firstBtn: this.getTranslation("genericDialogBox.button.deleteBtn"),
                secondBtn: this.getTranslation("genericDialogBox.button.cancelBtn"),
            },
            autoFocus: false,
            restoreFocus: false,
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.onDeleteAddress(address.id);
            }
        });
    }

    private onDeleteAddress(id: number): void {
        this.userService.deleteAddress(id).subscribe(
            (response) => {
                this.getUserDetails();
                this.openSnackBar(response, SnackBarConfig.Actions.OKAY);
            },
            (error) => {
                throw new Error(error.message);
            },
        );
    }

    private openSnackBar(message: string, action: string): void {
        this._snackBar.open(message, action, {
            ...SnackBarConfig.Defaults,
        });
    }

    public editAddress(address: AddressDetailsAttributes): void {
        this.editMode = true;
        this.addMode = false;
        this.selectedAddress = address;
    }

    public cancelEdit(): void {
        this.editMode = false;
    }

    public cancelAdd(): void {
        this.addMode = false;
    }

    public toggleAddAddressMode(): void {
        this.addMode = true;
        this.editMode = false;
    }

    public receiveAddressData(address: AddressDetailsAttributes): void {
        this.address = address;
    }

    public onUpdate(): void {
        const body = this.address;
        this.userService.updateAddress(body).subscribe({
            next: (response) => {
                this.getUserDetails();
                this.openSnackBar(response, SnackBarConfig.Actions.OKAY);
            },
            error: (error) => {
                throw new Error(error.message);
            },
        });
    }

    public onUpdateEmail(): void {
        const id = this.userDetails!.user.id;
        const email = this.profileForm.get("email")?.value;
        this.userService.updateEmail({ email, id }).subscribe({
            next: (response) => {
                this.getUserDetails();
                this.openSnackBar(response, SnackBarConfig.Actions.OKAY);
            },
            error: (error) => {
                throw new Error(error.message);
            },
        });
    }

    public onAdd(): void {
        const body = this.address;
        this.userService.addAddress(body).subscribe({
            next: (response) => {
                if (response) {
                    this.getUserDetails();
                    this.addMode = false;
                    this.openSnackBar(this.getTranslation("profile.successMessage.add"), SnackBarConfig.Actions.CLOSE);
                }
            },
            error: (error) => {
                throw new Error(error.message);
            },
        });
    }

    public get emailError(): "required" | "invalid" | null {
        const emailControl = this.profileForm.get("email");
        if (emailControl?.errors) {
            if (emailControl.hasError("required")) {
                return "required";
            } else {
                return "invalid";
            }
        }
        return null;
    }

    /* To set an address as default */
    public setAsDefault(addressID: number): void {
        this.userService.setDefaultAddress(addressID).subscribe({
            next: (response) => {
                if (response) {
                    this.openSnackBar(response.message, SnackBarConfig.Actions.CLOSE);
                    this.getUserDetails();
                }
            },
            error: (error) => {
                throw new Error(error.message);
            },
        });
    }

    public setActive(section: string) {
        this.activeSection = section;
    }

    public onAddressChange(id: number): void {
        this.selectAddressById(id);
    }

    private selectAddressById(id: number): void {
        const address = this.currentUserAddresses!.find((address) => address!.id === id);
        if (address) {
            this.selectedAddress = address;
            this.userService.updatePreferredAddress(id).subscribe({
                next: (response) => {
                    if (response) {
                        this.openSnackBar(response.message, "Close");
                    }
                },
                error: (error) => {
                    throw new Error(error.message);
                },
            });
        }
    }

    public addOrEditAddress(address?: BaseAddress): void {
        const addressId = address?.id;
        const dialogData = {
            componentName: DialogInvokingComponents.AddAddress,
            title: addressId ? "Edit Delivery Address" : "Add Delivery Address",
            withUserProfileDetails: false,
            hideAgeAndShowMobile: false,
            addressId,
        };
        this.dialogService
            .openDialog(dialogData, "90%", { disableClose: true })
            .afterClosed()
            .subscribe((response) => {
                if (response) {
                    if (addressId) {
                        this.userService.updateAddress(response.addressDetails).subscribe((response) => {
                            if (response) {
                                this.openSnackBar(response, "Close");
                            }
                            this.getUserDetails();
                        });
                    } else {
                        this.userService.addAddress(response.addressDetails).subscribe((response) => {
                            if (response) {
                                this.openSnackBar(UserDetailStatus.Add, "Close");
                            }
                            this.getUserDetails();
                        });
                    }
                }
            });
    }

    public goBack(): void {
        this.router.navigate(["/account"]);
    }
}
